import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FilterDto } from '@shared/src/components/core/maintenance/filter/FilterDtos';
import { FilterStripDto } from '@shared/src/components/core/maintenance/filter/FilterStripDtos';
import { LoadResultBlockDto } from '@shared/src/dtos/tenants/loadlog/LoadResultBlockDto';
import { LoadResultBlockMessageDto } from '@shared/src/dtos/tenants/loadlog/LoadResultBlockMessageDto';
import { LoadResultDto } from '@shared/src/dtos/tenants/loadlog/LoadResultDto';

/**
 * Mostra un LoadResultDto: blocks agrupats (acordió), amb filtre ràpid per codi
 * de block i cerca de text sobre els missatges. Substitueix la visualització
 * en iframe de la taula HTML antiga.
 */
@Component({
    selector: 'shared-loadresult-dialog',
    templateUrl: 'shared.loadresult.dialog.html',
    styleUrls: ['./shared.loadresult.dialog.scss']
})
export class SharedLoadResultDialog {

    private static readonly TYPE_ALL = 'ALL';

    public blocks: LoadResultBlockDto[] = [];
    public blockFilter: string = '';
    public textFilter: string = '';
    public typeFilter: FilterStripDto;

    private collapsed: { [block: string]: boolean } = {};

    constructor(private mdDialogRef: MatDialogRef<SharedLoadResultDialog>,
        @Inject(MAT_DIALOG_DATA) public data: LoadResultDto) {
        this.blocks = this.sortBlocks(data && data.blocks ? data.blocks : []);
        this.typeFilter = this.buildTypeFilter();
    }

    /**
     * 'ALL' és incompatible amb la resta (activar-lo desactiva els altres i
     * viceversa); els tipus (Error/Warning/Info/Debug) són compatibles entre
     * ells, per poder-ne activar N alhora.
     */
    private buildTypeFilter(): FilterStripDto {
        let severities = ['Error', 'Warning', 'Info', 'Debug'];
        let result = new FilterStripDto();
        result.add(FilterDto.build(SharedLoadResultDialog.TYPE_ALL, 'LOADLOG.RESULT.TYPE.ALL', '', 0, true, severities));
        severities.forEach((s, i) => {
            result.add(FilterDto.build(s, 'LOADLOG.RESULT.TYPE.' + s.toUpperCase(), '', i + 1, false,
                [SharedLoadResultDialog.TYPE_ALL]));
        });
        return result;
    }

    public onTypeFilterChange(): void {
        // El filterstrip ja s'ha mutat (toggle + incompatibilitats); no cal fer
        // res més, visibleBlocks() es torna a avaluar al següent cicle.
    }

    private isAllTypesSelected(): boolean {
        let active = FilterStripDto.getActiveKeys(this.typeFilter);
        // Si per algun motiu no queda res seleccionat, no filtrem per tipus.
        return active.length === 0 || active.indexOf(SharedLoadResultDialog.TYPE_ALL) >= 0;
    }

    private selectedTypes(): string[] {
        return FilterStripDto.getActiveKeys(this.typeFilter).filter(k => k !== SharedLoadResultDialog.TYPE_ALL);
    }

    private sortBlocks(blocks: LoadResultBlockDto[]): LoadResultBlockDto[] {
        let copy = blocks.slice();
        copy.sort((a, b) => {
            let oa = a.order || '';
            let ob = b.order || '';
            let ua = oa.indexOf('_') === 0;
            let ub = ob.indexOf('_') === 0;
            if (ua !== ub)
                return ua ? -1 : 1;
            return oa.localeCompare(ob);
        });
        copy.forEach(x => {
            x.messages = (x.messages || []).slice()
                .sort((m, n) => (m.order || '').localeCompare(n.order || ''));
        });
        return copy;
    }

    public visibleBlocks(): LoadResultBlockDto[] {
        let bf = this.blockFilter.trim().toLowerCase();
        let tf = this.textFilter.trim().toLowerCase();
        let allTypes = this.isAllTypesSelected();
        let types = this.selectedTypes();
        return this.blocks.filter(b => {
            if (!allTypes && types.indexOf(b.messageType) < 0)
                return false;
            if (bf !== '' && (b.block || '').toLowerCase().indexOf(bf) < 0)
                return false;
            if (tf !== '' && !this.matchesText(b, tf))
                return false;
            return true;
        });
    }

    public visibleMessages(b: LoadResultBlockDto): LoadResultBlockMessageDto[] {
        let tf = this.textFilter.trim().toLowerCase();
        let messages = b.messages || [];
        if (tf === '')
            return messages;
        return messages.filter(m => this.messageMatchesText(m, tf));
    }

    private matchesText(b: LoadResultBlockDto, tf: string): boolean {
        return (b.messages || []).some(m => this.messageMatchesText(m, tf));
    }

    private messageMatchesText(m: LoadResultBlockMessageDto, tf: string): boolean {
        return (m.message || '').toLowerCase().indexOf(tf) >= 0
            || (m.messageCode || '').toLowerCase().indexOf(tf) >= 0
            || (m.messageType || '').toLowerCase().indexOf(tf) >= 0;
    }

    public toggle(b: LoadResultBlockDto): void {
        this.collapsed[b.block] = !this.collapsed[b.block];
    }

    public isCollapsed(b: LoadResultBlockDto): boolean {
        return this.collapsed[b.block] === true;
    }

    public typeClass(messageType: string): string {
        switch ((messageType || '').toLowerCase()) {
            case 'error': return 'lr-error';
            case 'warning': return 'lr-warning';
            case 'info': return 'lr-info';
            case 'debug': return 'lr-debug';
            default: return 'lr-none';
        }
    }

    public onClose(): void {
        this.mdDialogRef.close();
    }
}
